import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import {
  JoomlaCategories,
  JoomlaContent,
  JoomlaEventBookingCategory,
  MoodleAgendaItem,
  MoodleEvent,
  MoodleGalleryImage,
  MoodleLocation,
  MoodleSpeaker,
  MoodleSponsor,
} from '../../types/moodleEventBooking';
import type {
  MoodleCategory,
  MoodleCourse,
  MoodleTag,
  MoodleTagArea,
  MoodleTagCollection,
  MoodleUser,
} from '../../types/moodleTypes';

export type MoodleInstanceKey = 'external' | 'internal';

interface QuizState {
  quizId: number | null;
  attemptId: number | null;
  answers: Record<string, any>;
  currentQuestion: number;
  questionOrder: string[];
  questionsPerPage: number;
  totalQuestions: number;
  reviewComplete: boolean;
}

export interface ProctorSummary {
  id: number;
  username: string;
  email?: string;
  name?: string;
}

export interface RoleAssignment {
  roleid: number;
  shortname: string;
  contextid: number;
}

interface MoodleUserState {
  id: number | null;
  data: MoodleUser | null;
  authenticated: boolean;
  authError: string | null;
  previousRoles: RoleAssignment[];
}

export interface MoodleInstanceState {
  courses: Record<number, MoodleCourse>;
  categories: Record<number, MoodleCategory>;
  events: Record<number, MoodleEvent>;
  users: Record<number, MoodleUser>;
  courseProctors: Record<number, ProctorSummary[]>;
  joomlaCategories: Record<number, JoomlaCategories>;
  joomlaEventBookingCategories: Record<number, JoomlaEventBookingCategory>;
  joomlaContent: Record<number, JoomlaContent>;
  speakers: Record<number, MoodleSpeaker>;
  sponsors: Record<number, MoodleSponsor>;
  agenda: Record<number, MoodleAgendaItem>;
  gallery: Record<number, MoodleGalleryImage>;
  locations: Record<number, MoodleLocation>;
  quiz: QuizState;
  user: MoodleUserState;
  selectedCategory: string;
  tags: Record<string, MoodleTag>;
  tagCollections: Record<number, MoodleTagCollection>;
  tagAreas: Record<number, MoodleTagArea>;
}

export interface MoodleState {
  activeInstance: MoodleInstanceKey;
  instances: Record<MoodleInstanceKey, MoodleInstanceState>;
}

const initialQuizState: QuizState = {
  quizId: null,
  attemptId: null,
  answers: {},
  currentQuestion: 0,
  questionOrder: [],
  questionsPerPage: 1,
  totalQuestions: 0,
  reviewComplete: false,
};

const initialUserState: MoodleUserState = {
  id: null,
  data: null,
  authenticated: false,
  authError: null,
  previousRoles: [],
};

export const selectMoodleInstanceState =
  (instance: MoodleInstanceKey) =>
    (state: RootState): MoodleInstanceState =>
      state.moodle.instances[instance] ?? createInitialInstanceState();

export const selectMoodleUserStateForInstance =
  (instance: MoodleInstanceKey) =>
    (state: RootState): MoodleUserState =>
      state.moodle.instances[instance]?.user ?? { ...initialUserState };

export const selectExternalMoodleUserState = (state: RootState) =>
  state.moodle.instances.external.user;

export const selectInternalMoodleUserState = (state: RootState) =>
  state.moodle.instances.internal.user;

const createInitialInstanceState = (): MoodleInstanceState => ({
  courses: {},
  categories: {},
  events: {},
  users: {},
  courseProctors: {},
  joomlaCategories: {},
  joomlaEventBookingCategories: {},
  joomlaContent: {},
  speakers: {},
  sponsors: {},
  agenda: {},
  gallery: {},
  locations: {},
  quiz: { ...initialQuizState },
  user: { ...initialUserState },
  selectedCategory: '',
  tags: {},
  tagCollections: {},
  tagAreas: {},
});

const initialState: MoodleState = {
  activeInstance: 'external',
  instances: {
    external: createInitialInstanceState(),
    internal: createInitialInstanceState(),
  },
};

type InstancePayload = {
  instance?: MoodleInstanceKey;
};

type HydrateCoursesPayload =
  | MoodleCourse[]
  | {
  courses?: MoodleCourse[];
  instance?: MoodleInstanceKey;
};

type HydrateCategoriesPayload =
  | MoodleCategory[]
  | {
  categories?: MoodleCategory[];
  instance?: MoodleInstanceKey;
};

type HydrateJoomlaContentPayload =
  | JoomlaContent[]
  | {
  content?: JoomlaContent[];
  instance?: MoodleInstanceKey;
};

type HydrateJoomlaCategoriesPayload =
  | JoomlaCategories[]
  | {
  categories?: JoomlaCategories[];
  instance?: MoodleInstanceKey;
};

type HydrateJoomlaEventBookingCategoriesPayload =
  | JoomlaEventBookingCategory[]
  | {
  categories?: JoomlaEventBookingCategory[];
  instance?: MoodleInstanceKey;
};

type HydrateUsersPayload =
  | MoodleUser[]
  | {
  users?: MoodleUser[];
  instance?: MoodleInstanceKey;
};

type HydrateCourseProctorsPayload =
  | Array<{ courseId: number; proctors: ProctorSummary[] }>
  | {
  items?: Array<{ courseId: number; proctors: ProctorSummary[] }>;
  instance?: MoodleInstanceKey;
};

type HydrateEventsPayload =
  | MoodleEvent[]
  | {
  events?: MoodleEvent[];
  instance?: MoodleInstanceKey;
};

type HydrateSpeakersPayload =
  | MoodleSpeaker[]
  | {
  speakers?: MoodleSpeaker[];
  instance?: MoodleInstanceKey;
};

type HydrateSponsorsPayload =
  | MoodleSponsor[]
  | {
  sponsors?: MoodleSponsor[];
  instance?: MoodleInstanceKey;
};

type HydrateAgendaPayload =
  | MoodleAgendaItem[]
  | {
  agenda?: MoodleAgendaItem[];
  instance?: MoodleInstanceKey;
};

type HydrateGalleryPayload =
  | MoodleGalleryImage[]
  | {
  gallery?: MoodleGalleryImage[];
  instance?: MoodleInstanceKey;
};

type HydrateLocationsPayload =
  | MoodleLocation[]
  | {
  locations?: MoodleLocation[];
  instance?: MoodleInstanceKey;
};

type HydrateTagsPayload =
  | MoodleTag[]
  | {
  tags?: MoodleTag[];
  instance?: MoodleInstanceKey;
};

type HydrateTagCollectionsPayload =
  | MoodleTagCollection[]
  | {
  collections?: MoodleTagCollection[];
  instance?: MoodleInstanceKey;
};

type HydrateTagAreasPayload =
  | MoodleTagArea[]
  | {
  areas?: MoodleTagArea[];
  instance?: MoodleInstanceKey;
};

type InitializeQuizPayload = {
  quizId: number;
  attemptId: number;
  questionOrder: string[];
  questionsPerPage: number;
  totalQuestions: number;
  instance?: MoodleInstanceKey;
};

type SetAnswerPayload = {
  questionId: string;
  answer: any;
  instance?: MoodleInstanceKey;
};

type SetCurrentQuestionPayload = {
  currentQuestion: number;
  instance?: MoodleInstanceKey;
};

type SetMoodleUserPayload = {
  id: number | null;
  data: MoodleUser | null;
  instance?: MoodleInstanceKey;
};

type AuthenticateMoodleUserPayload = {
  id?: number | null;
  data?: MoodleUser | null;
  instance?: MoodleInstanceKey;
};

type SetMoodleAuthErrorPayload =
  | string
  | {
  error?: string | null;
  instance?: MoodleInstanceKey;
};

type SetPreviousRolesPayload = {
  roles?: RoleAssignment[];
  instance?: MoodleInstanceKey;
};

type SetSelectedCategoryPayload =
  | string
  | {
  selectedCategory: string;
  instance?: MoodleInstanceKey;
};

function getInstanceState(state: MoodleState, instance?: MoodleInstanceKey) {
  return state.instances[instance ?? state.activeInstance ?? 'external'];
}

const moodleSlice = createSlice({
  name: 'moodle',
  initialState,
  reducers: {
    setActiveMoodleInstance(state, action: PayloadAction<MoodleInstanceKey>) {
      state.activeInstance = action.payload;
    },

    hydrateCourses(state, action: PayloadAction<HydrateCoursesPayload>) {
      const payload = Array.isArray(action.payload)
        ? { courses: action.payload, instance: 'external' as MoodleInstanceKey }
        : action.payload;
      const target = getInstanceState(state, payload.instance);
      (payload.courses ?? []).forEach((course) => {
        target.courses[course.id] = course;
      });
    },

    hydrateCategories(state, action: PayloadAction<HydrateCategoriesPayload>) {
      const payload = Array.isArray(action.payload)
        ? { categories: action.payload, instance: 'external' as MoodleInstanceKey }
        : action.payload;
      const target = getInstanceState(state, payload.instance);
      (payload.categories ?? []).forEach((category) => {
        target.categories[category.id] = category;
      });
    },

    hydrateJoomlaContent(state, action: PayloadAction<HydrateJoomlaContentPayload>) {
      const payload = Array.isArray(action.payload)
        ? { content: action.payload, instance: 'external' as MoodleInstanceKey }
        : action.payload;
      const target = getInstanceState(state, payload.instance);
      (payload.content ?? []).forEach((content) => {
        target.joomlaContent[content.id] = content;
      });
    },

    hydrateJoomlaCategories(state, action: PayloadAction<HydrateJoomlaCategoriesPayload>) {
      const payload = Array.isArray(action.payload)
        ? { categories: action.payload, instance: 'external' as MoodleInstanceKey }
        : action.payload;
      const target = getInstanceState(state, payload.instance);
      (payload.categories ?? []).forEach((category) => {
        target.joomlaCategories[category.id] = category;
      });
    },

    hydrateJoomlaEventBookingCategories(
      state,
      action: PayloadAction<HydrateJoomlaEventBookingCategoriesPayload>
    ) {
      const payload = Array.isArray(action.payload)
        ? { categories: action.payload, instance: 'external' as MoodleInstanceKey }
        : action.payload;
      const target = getInstanceState(state, payload.instance);
      (payload.categories ?? []).forEach((category) => {
        target.joomlaEventBookingCategories[category.id] = category;
      });
    },

    hydrateUsers(state, action: PayloadAction<HydrateUsersPayload>) {
      const payload = Array.isArray(action.payload)
        ? { users: action.payload, instance: 'external' as MoodleInstanceKey }
        : action.payload;
      const target = getInstanceState(state, payload.instance);
      (payload.users ?? []).forEach((user) => {
        target.users[user.id] = user;
      });
    },

    hydrateCourseProctors(state, action: PayloadAction<HydrateCourseProctorsPayload>) {
      const payload = Array.isArray(action.payload)
        ? { items: action.payload, instance: 'external' as MoodleInstanceKey }
        : action.payload;
      const target = getInstanceState(state, payload.instance);

      (payload.items ?? []).forEach(({ courseId, proctors }) => {
        const unique = [...(target.courseProctors[courseId] ?? []), ...proctors].reduce(
          (acc, p) => {
            acc.set(p.id, p);
            return acc;
          },
          new Map<number, ProctorSummary>()
        );

        target.courseProctors[courseId] = Array.from(unique.values()).sort((a, b) =>
          (a.username || '').localeCompare(b.username || '')
        );
      });
    },

    clearCourseProctors(state, action: PayloadAction<InstancePayload | undefined>) {
      const target = getInstanceState(state, action.payload?.instance);
      target.courseProctors = {};
    },

    hydrateEvents(state, action: PayloadAction<HydrateEventsPayload>) {
      const payload = Array.isArray(action.payload)
        ? { events: action.payload, instance: 'external' as MoodleInstanceKey }
        : action.payload;
      const target = getInstanceState(state, payload.instance);

      (payload.events ?? []).forEach((event) => {
        if (event.id == null) return;

        target.events[Number(event.id)] = event;

        (event as any).agenda?.forEach((a: MoodleAgendaItem) => {
          if (a?.id != null) target.agenda[a.id] = a;
        });

        (event as any).gallery?.forEach((g: MoodleGalleryImage) => {
          if (g?.id != null) target.gallery[g.id] = g;
        });

        (event as any).speakers?.forEach((s: MoodleSpeaker) => {
          if (s?.id != null) target.speakers[s.id] = s;
        });

        (event as any).sponsors?.forEach((s: MoodleSponsor) => {
          if (s?.id != null) target.sponsors[s.id] = s;
        });

        const loc: MoodleLocation | undefined =
          (event as any).location_details ?? (event as any).location;
        if (loc && typeof loc.id === 'number') {
          target.locations[loc.id] = loc;
        }

        const ebCategory: JoomlaEventBookingCategory | undefined = (event as any).category;
        if (ebCategory && typeof ebCategory.id === 'number') {
          target.joomlaEventBookingCategories[ebCategory.id] = ebCategory;
        }
      });
    },

    hydrateSpeakers(state, action: PayloadAction<HydrateSpeakersPayload>) {
      const payload = Array.isArray(action.payload)
        ? { speakers: action.payload, instance: 'external' as MoodleInstanceKey }
        : action.payload;
      const target = getInstanceState(state, payload.instance);
      (payload.speakers ?? []).forEach((speaker) => {
        if (speaker?.id != null) target.speakers[speaker.id] = speaker;
      });
    },

    hydrateSponsors(state, action: PayloadAction<HydrateSponsorsPayload>) {
      const payload = Array.isArray(action.payload)
        ? { sponsors: action.payload, instance: 'external' as MoodleInstanceKey }
        : action.payload;
      const target = getInstanceState(state, payload.instance);
      (payload.sponsors ?? []).forEach((sponsor) => {
        if (sponsor?.id != null) target.sponsors[sponsor.id] = sponsor;
      });
    },

    hydrateAgenda(state, action: PayloadAction<HydrateAgendaPayload>) {
      const payload = Array.isArray(action.payload)
        ? { agenda: action.payload, instance: 'external' as MoodleInstanceKey }
        : action.payload;
      const target = getInstanceState(state, payload.instance);
      (payload.agenda ?? []).forEach((item) => {
        if (item?.id != null) target.agenda[item.id] = item;
      });
    },

    hydrateGallery(state, action: PayloadAction<HydrateGalleryPayload>) {
      const payload = Array.isArray(action.payload)
        ? { gallery: action.payload, instance: 'external' as MoodleInstanceKey }
        : action.payload;
      const target = getInstanceState(state, payload.instance);
      (payload.gallery ?? []).forEach((item) => {
        if (item?.id != null) target.gallery[item.id] = item;
      });
    },

    hydrateLocations(state, action: PayloadAction<HydrateLocationsPayload>) {
      const payload = Array.isArray(action.payload)
        ? { locations: action.payload, instance: 'external' as MoodleInstanceKey }
        : action.payload;
      const target = getInstanceState(state, payload.instance);
      (payload.locations ?? []).forEach((location) => {
        if (location?.id != null) target.locations[location.id] = location;
      });
    },

    hydrateTags(state, action: PayloadAction<HydrateTagsPayload>) {
      const payload = Array.isArray(action.payload)
        ? { tags: action.payload, instance: 'external' as MoodleInstanceKey }
        : action.payload;
      const target = getInstanceState(state, payload.instance);
      (payload.tags ?? []).forEach((tag) => {
        target.tags[tag.name] = tag;
      });
    },

    hydrateTagCollections(state, action: PayloadAction<HydrateTagCollectionsPayload>) {
      const payload = Array.isArray(action.payload)
        ? { collections: action.payload, instance: 'external' as MoodleInstanceKey }
        : action.payload;
      const target = getInstanceState(state, payload.instance);
      (payload.collections ?? []).forEach((collection) => {
        target.tagCollections[collection.id] = collection;
      });
    },

    hydrateTagAreas(state, action: PayloadAction<HydrateTagAreasPayload>) {
      const payload = Array.isArray(action.payload)
        ? { areas: action.payload, instance: 'external' as MoodleInstanceKey }
        : action.payload;
      const target = getInstanceState(state, payload.instance);
      (payload.areas ?? []).forEach((area) => {
        target.tagAreas[area.id] = area;
      });
    },

    initializeQuiz(state, action: PayloadAction<InitializeQuizPayload>) {
      const payload = action.payload;
      const target = getInstanceState(state, payload.instance);
      target.quiz.quizId = payload.quizId;
      target.quiz.attemptId = payload.attemptId;
      target.quiz.questionOrder = payload.questionOrder;
      target.quiz.questionsPerPage = payload.questionsPerPage;
      target.quiz.totalQuestions = payload.totalQuestions;
      target.quiz.currentQuestion = 0;
      target.quiz.answers = {};
      target.quiz.reviewComplete = false;
    },

    setAnswer(state, action: PayloadAction<SetAnswerPayload>) {
      const payload = action.payload;
      const target = getInstanceState(state, payload.instance);
      target.quiz.answers[payload.questionId] = payload.answer;
    },

    setCurrentQuestion(state, action: PayloadAction<SetCurrentQuestionPayload>) {
      const payload = action.payload;
      const target = getInstanceState(state, payload.instance);
      target.quiz.currentQuestion = payload.currentQuestion;
    },

    markReviewComplete(state, action: PayloadAction<InstancePayload | undefined>) {
      const target = getInstanceState(state, action.payload?.instance);
      target.quiz.reviewComplete = true;
    },

    resetQuiz(state, action: PayloadAction<InstancePayload | undefined>) {
      const target = getInstanceState(state, action.payload?.instance);
      target.quiz = { ...initialQuizState };
    },

    setMoodleUser(state, action: PayloadAction<SetMoodleUserPayload>) {
      const payload = action.payload;
      const target = getInstanceState(state, payload.instance);
      target.user.id = payload.id ?? null;
      target.user.data = payload.data ?? null;
      target.user.authenticated = !!payload.id && !!payload.data;
      target.user.authError = null;
    },

    authenticateMoodleUser(state, action: PayloadAction<AuthenticateMoodleUserPayload | undefined>) {
      const payload = action.payload ?? {};
      const target = getInstanceState(state, payload.instance);
      target.user.authenticated = true;

      if (payload.id !== undefined) target.user.id = payload.id ?? null;
      if (payload.data !== undefined) target.user.data = payload.data ?? null;

      target.user.authError = null;
    },

    setMoodleAuthError(state, action: PayloadAction<SetMoodleAuthErrorPayload>) {
      const payload =
        typeof action.payload === 'string'
          ? { error: action.payload, instance: 'external' as MoodleInstanceKey }
          : action.payload;
      const target = getInstanceState(state, payload.instance);
      target.user.authError = payload.error || null;
      target.user.authenticated = false;
      target.user.id = null;
      target.user.data = null;
      target.user.previousRoles = [];
    },

    clearMoodleUser(state) {
      state.instances.external.user = { ...initialUserState };
      state.instances.internal.user = { ...initialUserState };
    },

    clearMoodleUserForInstance(state, action: PayloadAction<MoodleInstanceKey>) {
      const target = getInstanceState(state, action.payload);
      target.user = { ...initialUserState };
    },

    setPreviousRoles(state, action: PayloadAction<SetPreviousRolesPayload>) {
      const payload = action.payload;
      const target = getInstanceState(state, payload.instance);
      target.user.previousRoles = payload.roles ?? [];
    },

    clearPreviousRoles(state, action: PayloadAction<InstancePayload | undefined>) {
      const target = getInstanceState(state, action.payload?.instance);
      target.user.previousRoles = [];
    },

    setSelectedCategory(state, action: PayloadAction<SetSelectedCategoryPayload>) {
      const payload =
        typeof action.payload === 'string'
          ? { selectedCategory: action.payload, instance: 'external' as MoodleInstanceKey }
          : action.payload;
      const target = getInstanceState(state, payload.instance);
      target.selectedCategory = payload.selectedCategory;
    },

    resetMoodleInstance(state, action: PayloadAction<MoodleInstanceKey>) {
      state.instances[action.payload] = createInitialInstanceState();
    },

    resetMoodle() {
      return { ...initialState };
    },
  },
});

export const selectMoodleRoot = (state: RootState) => state.moodle;

export const selectActiveMoodleInstanceKey = (state: RootState): MoodleInstanceKey =>
  state.moodle.activeInstance ?? 'external';

export const selectActiveMoodleInstanceState = (state: RootState): MoodleInstanceState =>
  state.moodle.instances[selectActiveMoodleInstanceKey(state)] ?? createInitialInstanceState();

export const selectMoodleCoursesMap = (state: RootState) =>
  selectActiveMoodleInstanceState(state).courses;

export const selectMoodleCategoriesMap = (state: RootState) =>
  selectActiveMoodleInstanceState(state).categories;

export const selectMoodleEventsMap = (state: RootState) =>
  selectActiveMoodleInstanceState(state).events;

export const selectMoodleUsersMap = (state: RootState) =>
  selectActiveMoodleInstanceState(state).users;

export const selectMoodleUserState = (state: RootState) =>
  selectActiveMoodleInstanceState(state).user;

export const selectMoodleSelectedCategory = (state: RootState) =>
  selectActiveMoodleInstanceState(state).selectedCategory;

export const selectMoodleSpeakersMap = (state: RootState) =>
  selectActiveMoodleInstanceState(state).speakers;

export const selectMoodleSponsorsMap = (state: RootState) =>
  selectActiveMoodleInstanceState(state).sponsors;

export const selectMoodleLocationsMap = (state: RootState) =>
  selectActiveMoodleInstanceState(state).locations;

export const selectMoodleJoomlaCategoriesMap = (state: RootState) =>
  selectActiveMoodleInstanceState(state).joomlaCategories;

export const selectMoodleJoomlaEventBookingCategoriesMap = (state: RootState) =>
  selectActiveMoodleInstanceState(state).joomlaEventBookingCategories;

export const selectMoodleGalleryMap = (state: RootState) =>
  selectActiveMoodleInstanceState(state).gallery;

export const selectMoodleTagAreasMap = (state: RootState) =>
  selectActiveMoodleInstanceState(state).tagAreas;

export const selectMoodleTagCollectionsMap = (state: RootState) =>
  selectActiveMoodleInstanceState(state).tagCollections;

export const selectMoodleTagsMap = (state: RootState) =>
  selectActiveMoodleInstanceState(state).tags;

export const {
  setActiveMoodleInstance,
  hydrateCourses,
  hydrateCategories,
  hydrateJoomlaContent,
  hydrateJoomlaCategories,
  hydrateJoomlaEventBookingCategories,
  hydrateEvents,
  hydrateUsers,
  hydrateCourseProctors,
  clearCourseProctors,
  hydrateSpeakers,
  hydrateSponsors,
  hydrateAgenda,
  hydrateGallery,
  hydrateLocations,
  hydrateTags,
  hydrateTagCollections,
  hydrateTagAreas,
  initializeQuiz,
  setAnswer,
  setSelectedCategory,
  setCurrentQuestion,
  markReviewComplete,
  resetQuiz,
  setMoodleUser,
  setMoodleAuthError,
  clearMoodleUser,
  clearMoodleUserForInstance,
  authenticateMoodleUser,
  setPreviousRoles,
  clearPreviousRoles,
  resetMoodleInstance,
  resetMoodle,
} = moodleSlice.actions;

export default moodleSlice.reducer;